import React from 'react';
import styled from 'styled-components';
import ImagePreviewer from 'components/image-previewer';
import EmbedViewer from 'components/embed-viewer';
import Button from 'components/button';
import Icon from 'components/icon';
import Image from 'components/image';

const Wrapper = styled.div`
  overflow-y: auto;
  &.gallerySlider {
    border: none;
    overflow: hidden;
    height: auto;
    padding: 0px;
    .mediaContainer {
      border-radius: 0px;
      height: 400px;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 5px;
  height: 500px;
  &.grid-1 {
    grid-template-columns: 1fr;
  }
  &.grid-2 {
    grid-template-columns: 1fr 1fr;
  }
  &.grid-3 {
    grid-template-rows: 1fr 1fr;
    grid-template-columns: 2fr 1fr 1fr;
    div {
      &:nth-child(1) {
        grid-row: span 2;
      }
      &:nth-child(2) {
        grid-column: span 2;
      }
      &:nth-child(3) {
        grid-column: span 2;
      }
    }
  }
  &.grid-4 {
    grid-template-columns: 2fr 1fr 1fr;
    div {
      &:nth-child(1) {
        grid-row: span 2;
      }
      &:nth-child(4) {
        grid-column: span 2;
      }
    }
  }
  &.grid-5 {
    grid-template-rows: 1fr 1fr;
    grid-template-columns: 2fr 1fr 1fr;
    div {
      &:nth-child(1) {
        grid-row: span 2;
      }
    }
  }
`;

const Slider = styled.div`
  height: 400px;
  position: relative;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  button {
    position: absolute;
    width: 35px;
    height: 35px;
    background-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.18), 0px 0px 0px 1px rgba(0, 0, 0, 0.08);
    color: #000;
    &:first-child {
      left: 20px;
      top: 50%;
      transform: translateY(-50%);
    }
    &:last-child {
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  .imageCount {
    position: absolute;
    bottom: 10px;
    right: 10px;
    padding: 3px 12px;
    background-color: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 14px;
    border-radius: 4px;
    pointer-events: none;
  }
`;

const MediaWrapper = styled.div`
  position: relative;
  cursor: pointer;
  overflow: hidden;
  &:hover {
    opacity: 0.8;
  }
  .smartImage {
    width: 100%;
    height: 100%;
  }
  &.mediaHidden {
    display: none;
  }
  &.media-virtualTour,
  &.previewLarge {
  }
  &:first-child {
  }
  iframe {
    width: 100%;
    height: 100%;
  }
  transition: all 0.2s;
`;

const PlusCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 30px;
  width: 100%;
  height: 100%;
`;

const parseVideo = (url = '') => {
  const position = url.match(
    /(http:|https:|)\/\/(player.|www.)?(vimeo\.com|youtu(be\.com|\.be|be\.googleapis\.com))\/(video\/|embed\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(&\S+)?/
  );
  let type;
  if (position?.[3]?.indexOf('youtu') > -1) {
    type = 'youtube';
  } else if (position?.[3]?.indexOf('vimeo') > -1) {
    type = 'vimeo';
  }

  return {
    type: type,
    id: position?.[6]
  };
};

const getEmbedUrl = (url) => {
  const videoObj = parseVideo(url);
  if (videoObj.type === 'youtube') {
    return `https://www.youtube.com/embed/${videoObj.id}`;
  } else if (videoObj.type === 'vimeo') {
    return `https://player.vimeo.com/video/${videoObj.id}`;
  }
  return url;
};

const Embed = ({ url }) => {
  return <iframe src={getEmbedUrl(url)} title="Embedded media" allowFullScreen />;
};

const MediaRender = ({
  media = {},
  onImageClick,
  onEmbedClick,
  hidden,
  optimizeRender = true,
  plusCount
}) => {
  if (hidden && optimizeRender) return null;
  const isEmbed = ['virtualTour', 'video'].includes(media.type);
  return (
    <MediaWrapper className={`media-${media.type} mediaContainer ${hidden ? 'mediaHidden' : ''}`}>
      {media.type === 'image' && (
        <Image
          onClick={onImageClick}
          url={media.url}
          fallback="/assets/coming-soon-image-small.png"
        />
      )}
      {isEmbed && <Embed url={media.url} />}
      {isEmbed && (
        <PlusCount
          onClick={(event) => {
            event.stopPropagation();
            onEmbedClick(getEmbedUrl(media.url));
          }}
          style={{ cursor: 'pointer' }}
        />
      )}
      {plusCount > 0 && <PlusCount onClick={onImageClick}>{`+${plusCount}`}</PlusCount>}
    </MediaWrapper>
  );
};

const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const enterFullscreen = () => {
  if (isEmbedded() && document.documentElement.requestFullscreen) {
    document.documentElement.requestFullscreen().catch(() => {});
  }
};

const exitFullscreen = () => {
  if (document.fullscreenElement && document.exitFullscreen) {
    document.exitFullscreen().catch(() => {});
  }
};

export default ({ isSlider, location: { displayInfo: { media = [] } = {} } = {} }) => {
  const [showPreviewer, setShowPreviewer] = React.useState(false);
  const [previewStart, setPreviewStart] = React.useState(0);
  const [embedUrl, setEmbedUrl] = React.useState(null);
  const [currentPos, setCurrentPos] = React.useState(0);

  // The previewer receives images only, while `media` mixes images and embeds —
  // remap a media index to its position within the images-only list
  const toImageIndex = (mediaIndex) =>
    Math.max(0, media.slice(0, mediaIndex + 1).filter((m) => m.type === 'image').length - 1);

  const handleOpenPreviewer = (mediaIndex) => {
    enterFullscreen();
    setPreviewStart(toImageIndex(typeof mediaIndex === 'number' ? mediaIndex : currentPos));
    setShowPreviewer(true);
  };

  const handleClosePreviewer = () => {
    setShowPreviewer(false);
    exitFullscreen();
  };

  const handleOpenEmbed = (url) => {
    enterFullscreen();
    setEmbedUrl(url);
  };

  const handleCloseEmbed = () => {
    setEmbedUrl(null);
    exitFullscreen();
  };

  const handleNextPrev = (dir) => {
    const nextPos = dir === 'next' ? currentPos + 1 : currentPos - 1;
    setCurrentPos(nextPos === media.length ? 0 : nextPos < 0 ? media.length - 1 : nextPos);
  };

  const gridClass = `grid-${Math.min(media.length || 0, 5)}`;
  const plusCount = media.length > 5 ? media.length - 5 : 0;

  return (
    <Wrapper className={isSlider ? 'gallerySlider' : ''}>
      {isSlider && (
        <Slider onClick={handleOpenPreviewer}>
          {media.map((item, i) => (
            <MediaRender
              key={item.url}
              media={item}
              onImageClick={handleOpenPreviewer}
              onEmbedClick={handleOpenEmbed}
              hidden={currentPos !== i}
            />
          ))}
          {media.length > 1 && (
            <div>
              <Button
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleNextPrev('prev');
                }}>
                <Icon name="fa-solid fa-arrow-left" />
              </Button>
              <Button
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleNextPrev('next');
                }}>
                <Icon name="fa-solid fa-arrow-right" />
              </Button>
            </div>
          )}
          <div className="imageCount">
            {currentPos + 1}/{media.length}
          </div>
        </Slider>
      )}
      {!isSlider && (
        <Grid className={gridClass}>
          {media.slice(0, 5).map((item, i) => (
            <MediaRender
              key={item.url}
              media={item}
              onImageClick={() => handleOpenPreviewer(i)}
              onEmbedClick={handleOpenEmbed}
              plusCount={i === 4 ? plusCount : undefined}
            />
          ))}
        </Grid>
      )}
      <ImagePreviewer
        open={showPreviewer}
        urls={media.filter((m) => m.type === 'image').map((photo) => photo.url)}
        startIndex={previewStart}
        onClose={handleClosePreviewer}
      />
      <EmbedViewer open={!!embedUrl} url={embedUrl} onClose={handleCloseEmbed} />
    </Wrapper>
  );
};
